import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useAuthDialog } from "@/context/AuthDialogContext";

export const RETURNING_KEY = "cpenz_returning";
export const markReturning = () => localStorage.setItem(RETURNING_KEY, "1");
export const isReturning = () => localStorage.getItem(RETURNING_KEY) === "1";

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.11, delayChildren: 0.05 } } };
export const heroItem = {
  hidden: { opacity: 0, y: 34, filter: "blur(6px)" },
  show: { opacity: 1, y: 0, filter: "blur(0px)", transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] } },
};

/** Local-time greeting for the device: morning/afternoon → buongiorno, evening/night → buonasera. */
export function Greeting({ name, dark = false }) {
  const { lang } = useLang();
  const h = new Date().getHours();
  const evening = h >= 18 || h < 5;
  const word = lang === "IT" ? (evening ? "Buonasera" : "Buongiorno") : evening ? "Good evening" : "Good morning";
  return (
    <motion.p variants={heroItem} className={`text-lg sm:text-xl font-medium ${dark ? "text-white/70" : "text-[#6e6e73]"}`} data-testid="home-greeting">
      {word}{name ? `, ${name}` : ""} <span aria-hidden="true" data-no-invert className="inline-block">👋</span>
    </motion.p>
  );
}

/** "Cosa hai in mente?" + CREATE / INVEST. Same block on the landing and in the control panel. `dark` = inside the black hero panel. */
export function PathChoice({ greetingName, compact = false, dark = false }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const { user } = useAuth();
  const authDialog = useAuthDialog();
  const requireAuth = (to) => (user ? navigate(to) : authDialog.openAuth(() => navigate(to)));
  const choice = dark
    ? "group rounded-[22px] bg-white/[0.07] border border-white/10 p-6 text-left transition-[background-color,transform] duration-300 hover:bg-white/[0.12] hover:-translate-y-0.5 sm:p-8 text-white"
    : "group rounded-[22px] bg-white p-6 text-left shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-black/[0.04] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-24px_rgba(0,0,0,0.25)] sm:p-8 text-[#1d1d1f]";
  const badge = () => `inline-flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors ${dark ? "bg-white/10 text-white group-hover:bg-[#7c3aed]" : "bg-[#f5f5f7] text-[#1d1d1f] group-hover:bg-[#7c3aed] group-hover:text-white"}`;
  const sub = dark ? "text-white/60" : "text-[#86868b]";
  return (
    <motion.div initial="hidden" animate="show" variants={stagger} className="text-center" data-testid="path-choice">
      {greetingName !== undefined && <Greeting name={greetingName} dark={dark} />}
      <motion.h1 variants={heroItem} className={`cpenz-display ${dark ? "text-white" : "text-[#1d1d1f]"} ${compact ? "mt-1 text-[2.2rem] sm:text-5xl" : "text-[2.6rem] leading-[1.05] sm:text-6xl lg:text-[64px]"}`}>{t("home.title")}</motion.h1>
      <motion.p variants={heroItem} className={`${compact ? "mt-3" : "mt-6"} text-base sm:text-lg ${sub} max-w-xl mx-auto leading-relaxed`}>{t("hero.choose_sub")}</motion.p>
      <motion.div variants={heroItem} className={`${compact ? "mt-6" : "mt-10"} grid gap-4 sm:grid-cols-2 max-w-2xl mx-auto`}>
        <button type="button" onClick={() => requireAuth("/app/new")} data-testid="hero-choice-create" className={choice}>
          <span className="flex items-center justify-between gap-3"><span className="text-xl font-semibold tracking-tight">{t("hero.create")}</span><span className={badge()}>1</span></span>
          <span className={`mt-3 block text-sm leading-relaxed ${sub}`}>{t("hero.create_d")}</span>
        </button>
        <button type="button" onClick={() => requireAuth("/app/build")} data-testid="hero-choice-build" className={choice}>
          <span className="flex items-center justify-between gap-3"><span className="text-xl font-semibold tracking-tight">{t("hero.build")}</span><span className={badge()}>2</span></span>
          <span className={`mt-3 block text-sm leading-relaxed ${sub}`}>{t("hero.build_d")}</span>
        </button>
      </motion.div>
      <motion.p variants={heroItem} className={`${compact ? "mt-5" : "mt-8"} text-base sm:text-lg ${sub} leading-relaxed`} data-testid="hero-tagline">{t("hero.tagline")}</motion.p>
    </motion.div>
  );
}
